import { useState, useRef, useEffect } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus, Mail, Columns2, Columns3, Columns4, Square, Monitor, Tablet, Smartphone } from 'lucide-react';
import { cn } from '../../utils';
import { useEditorStore } from '../../store';
import { CanvasRow } from './CanvasRow';

const EMAIL_MAX_WIDTH = '640px';

const PREVIEW_WIDTHS: Record<string, string> = {
  desktop: '100%',
  tablet: '600px',
  mobile: '375px',
};

const PREVIEW_ICONS: Record<string, React.ElementType> = {
  desktop: Monitor,
  tablet: Tablet,
  mobile: Smartphone,
};

const COLUMN_LAYOUTS = [
  { cols: 1, icon: Square, label: '1 Column' },
  { cols: 2, icon: Columns2, label: '2 Columns' },
  { cols: 3, icon: Columns3, label: '3 Columns' },
  { cols: 4, icon: Columns4, label: '4 Columns' },
];

function AddRowPicker({ afterRowId, onClose }: { afterRowId?: string; onClose: () => void }) {
  const { addRowWithLayout } = useEditorStore();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-2.5
        bg-surface dark:bg-dark-800
        border border-line/80 dark:border-dark-600
        rounded-2xl shadow-2xl shadow-violet-100/60 dark:shadow-violet-900/30 p-3.5"
    >
      <p className="text-xs font-bold uppercase tracking-widest
        text-content-subtle dark:text-dark-500 text-center mb-3">
        Choose Layout
      </p>
      <div className="flex gap-2">
        {COLUMN_LAYOUTS.map(({ cols, icon: Icon, label }) => (
          <button
            key={cols}
            title={label}
            onClick={(e) => {
              e.stopPropagation();
              addRowWithLayout(cols, afterRowId);
              onClose();
            }}
            className="group flex flex-col items-center gap-2 px-3 py-3 rounded-xl
              border border-line-subtle dark:border-dark-700
              hover:border-violet-300 dark:hover:border-violet-500/40
              hover:bg-violet-50 dark:hover:bg-violet-900/20
              hover:shadow-md transition-all duration-150 min-w-[60px]"
          >
            <Icon className="size-5 text-content-subtle dark:text-dark-500
              group-hover:text-violet-500 dark:group-hover:text-violet-400 transition-colors" />
            <span className="text-xs font-semibold text-content-muted dark:text-dark-400
              group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors whitespace-nowrap">
              {label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function Canvas() {
  const { design, selectElement, previewMode } = useEditorStore();
  const [pickerOpen, setPickerOpen] = useState(false);

  const { isOver, setNodeRef } = useDroppable({
    id: 'canvas-root',
    data: { type: 'canvas' },
  });

  const isEmpty = design.body.rows.length === 0;
  const previewWidth = PREVIEW_WIDTHS[previewMode] ?? '100%';
  const PreviewIcon = PREVIEW_ICONS[previewMode] ?? Monitor;

  return (
    <div
      className="flex-1 overflow-auto flex flex-col canvas-bg"
      onClick={() => { selectElement(null); setPickerOpen(false); }}
    >
      {/* ── Sticky device preview badge ── */}
      <div className="sticky top-0 z-10 flex justify-center pt-4 pb-2 pointer-events-none">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full
          bg-white/90 dark:bg-dark-800/90 backdrop-blur-md
          border border-line/70 dark:border-dark-600/60
          shadow-md shadow-gray-200/60 dark:shadow-black/30
          text-xs font-semibold text-content-secondary dark:text-dark-300">
          <PreviewIcon className="size-3.5 text-violet-500 dark:text-violet-400" />
          <span className="capitalize">{previewMode} Preview</span>
          {previewWidth !== '100%' && (
            <>
              <span className="w-px h-3 bg-gray-200 dark:bg-dark-600" />
              <span className="text-violet-500 dark:text-violet-400 font-bold">{previewWidth}</span>
            </>
          )}
        </div>
      </div>

      {/* ── Centered email canvas ── */}
      <div className="flex justify-center px-8 pb-10">
        <div
          style={{ width: previewWidth, maxWidth: EMAIL_MAX_WIDTH }}
          className="w-full transition-all duration-300"
        >
          {/* The actual email card */}
          <div
            ref={setNodeRef}
            className={cn(
              'relative w-full transition-all duration-200',
              'bg-surface dark:bg-dark-800',
              'rounded-xl',
              'shadow-[0_2px_16px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.05)]',
              'dark:shadow-[0_12px_48px_rgba(0,0,0,0.50),0_0_0_1px_rgba(139,92,246,0.20)]',
              isOver && 'ring-2 ring-violet-400 ring-offset-4 dark:ring-offset-dark-950',
              isEmpty && 'flex flex-col items-center justify-center min-h-[420px]'
            )}
          >
            {/* Violet top accent bar — own clipping context so overflow-hidden doesn't cut row buttons */}
            <div className="absolute inset-x-0 top-0 h-[2px] rounded-t-xl overflow-hidden z-10 pointer-events-none">
              <div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500" />
            </div>
            {isEmpty ? (
              /* ── Compact clean empty state ── */
              <div className="flex flex-col items-center gap-4 py-12 px-8 text-center pointer-events-none w-full">
                {/* Icon */}
                <div className="w-12 h-12 rounded-2xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <Mail className="size-6 text-violet-500 dark:text-violet-400" />
                </div>

                {/* Text */}
                <div className="space-y-1.5">
                  <h3 className="text-sm font-bold text-gray-800 dark:text-white">
                    Build your email template
                  </h3>
                  <p className="text-xs text-content-subtle dark:text-dark-500 leading-relaxed max-w-[220px] mx-auto">
                    Drag elements from the left panel, or add your first row below.
                  </p>
                </div>

                {/* CTA */}
                <div className="relative pointer-events-auto">
                  <button
                    onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl
                      bg-violet-600 hover:bg-violet-700
                      text-white text-xs font-bold
                      shadow-md shadow-violet-400/20 dark:shadow-violet-900/30
                      hover:shadow-lg hover:-translate-y-0.5 transition-all duration-150"
                  >
                    <Plus className="size-3.5" />
                    Add First Row
                  </button>
                  {pickerOpen && <AddRowPicker onClose={() => setPickerOpen(false)} />}
                </div>
              </div>
            ) : (
              <SortableContext
                items={design.body.rows.map((r) => r.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-px">
                  {design.body.rows.map((row) => (
                    <CanvasRow key={row.id} row={row} />
                  ))}
                </div>
              </SortableContext>
            )}
          </div>


        </div>
      </div>
    </div>
  );
}
